// Les trois compositions : une tuile, un graphique, une carte. Chacune produit un PNG
// autoporteur — commune, intitulé, valeur, SOURCE ET MILLÉSIME, signature. Leurs jumelles
// SVG (tuile, graphique — pas la carte) vivent dans `imageSvg.ts` et reprennent les mêmes
// blocs.
//
// La source et le millésime ne sont pas décoratifs. L'image circule hors du site, sans
// notre interface autour : sans eux, un chiffre partagé devient un chiffre sans origine,
// et la doctrine « zéro invention, la source voyage avec la donnée » s'arrêterait au bord
// de l'écran. Ils sont déjà là, dans la note de la tuile — on les recopie, on ne les
// reconstruit pas.

import type { Map as CarteMapLibre } from 'maplibre-gl'

import { composerPng, texteAttribution, type Bloc } from './dessin'
import { graphiqueVivant, rejouer } from './rejeu'
// Depuis `../format` et `../comparaison`, jamais `../components` : ce dernier nous importe,
// et l'import croisé formait un cycle — il tient tant que l'appel est tardif, et casse le
// jour où le découpage en paquets change l'ordre d'initialisation. En production seulement.
import { NOM_PRODUIT } from '../../constants'
import { espaceAvantUnite, fmt, fmtDelta, uniteComparaison } from '../format'
import { construireComparaison, texteLibelle, texteValeur } from '../comparaison'
import type { Kpi, SectionChart, TemoinKpi } from '../types'

const NBSP = ' '

export interface ContexteImage {
  /** Nom de la commune, en tête de l'image — le premier mot que lit le destinataire. */
  commune: string
}

/** Note de bas de tuile : elle porte déjà la source et le millésime. */
export const noteOuSource = (note?: string | null, sources?: string[]): string | undefined => {
  if (note && note.trim()) return note.trim()
  if (sources && sources.length) return `Source : ${sources.join(' · ')}`
  return undefined
}

/**
 * Une tuile : redessinée à partir de la donnée en mémoire, jamais capturée depuis le DOM.
 *
 * Les lignes de comparaison passent par `construireComparaison` — LA MÊME fonction que
 * l'écran. C'est le correctif de la revue croisée du 22/09 : cette composition en avait
 * écrit sa propre version, et elle divergeait sur trois points, tous silencieux. Sur un
 * STOCK, l'écran montre la part de la commune dans le périmètre (« 65,9 % ») pendant que
 * l'image envoyait le TOTAL du périmètre sous l'unité de la tuile. Un maire qui partageait
 * « Ménages » envoyait donc des chiffres qui n'étaient pas ceux qu'il avait sous les yeux.
 */
export function composerTuile(kpi: Kpi, ctx: ContexteImage, sources?: string[]): Promise<Blob> {
  return composerPng(blocsTuile(kpi, ctx, sources))
}

/** Les blocs d'une tuile — UNE liste pour les deux formats (PNG ici, SVG dans
 *  `imageSvg.ts`) : le fichier vectoriel dit exactement ce que dit l'image. */
export function blocsTuile(kpi: Kpi, ctx: ContexteImage,
                           sources?: string[]): Exclude<Bloc, { type: 'image' }>[] {
  const blocs: Exclude<Bloc, { type: 'image' }>[] = [
    { type: 'surtitre', texte: ctx.commune },
    { type: 'titre', texte: kpi.label },
    { type: 'valeur',
      texte: fmt(kpi.value, kpi.unit, kpi.decimales),
      unite: kpi.unit ?? undefined },
  ]
  if (kpi.secondaire) {
    if (kpi.annee != null) blocs.push({ type: 'note', texte: String(kpi.annee) })
    blocs.push({ type: 'note', texte: `${fmt(kpi.secondaire.value, kpi.secondaire.unit, 1)} ${kpi.secondaire.unit}` })
  }
  const compare = kpi.secondaire ?? kpi
  const comp = compare.comparaison ? construireComparaison(compare.comparaison, compare.unit) : null
  if (comp) {
    blocs.push({ type: 'lignes', lignes: comp.lignes.map(l => ({
      gauche: texteLibelle(l), droite: texteValeur(l),
    })) })
    // Le pied du bloc part AVEC les lignes : sur un stock, « part de la commune dans
    // chaque périmètre » est ce qui rend les quatre pourcentages lisibles. Sans lui, une
    // image montrerait « 65,9 % » face à « Essonne » sans dire de quoi c'est la part.
    for (const n of comp.notes) blocs.push({ type: 'note', texte: n })
    if (comp.reserve) blocs.push({ type: 'note', texte: comp.reserve })
  }
  blocs.push(...blocsTemoins(compare.temoins, compare.unit))
  const note = noteOuSource(kpi.note, sources)
  if (note) blocs.push({ type: 'note', texte: note })
  // CDC GBM § 3.3 (PX-4) : la limite écrite sous le chiffre voyage avec l'image (PNG et SVG
  // partagent ces blocs) — revue Codex de #1029.
  if (kpi.limite && kpi.limite.trim()) blocs.push({ type: 'note', texte: kpi.limite.trim() })
  return blocs
}

/**
 * Les communes témoins d'une tuile (CDC « témoins partout », étape 8). L'image les oubliait :
 * un maire qui partageait sa tuile envoyait la valeur et les échelles, pas les pairs qu'il
 * avait choisis et qu'il voyait juste en dessous. Elles suivent l'écran (`TemoinsKpi`) : même
 * ordre, même unité abrégée, même espace avant l'unité, même écart sans unité, « N/D » pour
 * une valeur absente. Le motif d'un « N/D », qui vit à l'écran dans une infobulle, s'écrit
 * ici en note : une image n'a pas de survol, et un « N/D » sans motif ne dit rien.
 */
function blocsTemoins(temoins: TemoinKpi[] | undefined,
                      uniteKpi?: string): Exclude<Bloc, { type: 'image' }>[] {
  if (!temoins?.length) return []
  const unite = uniteComparaison(uniteKpi)
  const valeur = (t: TemoinKpi): string => {
    if (t.valeur === null) return 'N/D'
    const nombre = fmt(t.valeur, unite) + (unite ? `${espaceAvantUnite(unite)}${unite}` : '')
    return t.ecart_commune != null ? `${nombre} (${fmtDelta(t.ecart_commune, unite)})` : nombre
  }
  const blocs: Exclude<Bloc, { type: 'image' }>[] = [{ type: 'lignes', lignes: temoins.map(t => ({
    gauche: t.origine === 'propose' ? `${t.nom} ·${NBSP}proposée` : t.nom,
    droite: valeur(t),
  })) }]
  for (const t of temoins) {
    const motif = t.valeur === null ? t.raison_nd?.trim() : undefined
    if (motif) blocs.push({ type: 'note', texte: `${t.nom}${NBSP}: N/D, ${motif}` })
  }
  // La note de l'écran renvoie à la page Comparaison, qu'une image ne peut pas ouvrir : on
  // n'en garde que ce qui explique le mot « proposée ».
  if (temoins.some(t => t.origine === 'propose')) {
    blocs.push({ type: 'note',
                 texte: `proposée${NBSP}: commune de strate comparable, ajoutée par ${NOM_PRODUIT}` })
  }
  return blocs
}

/** Densité du PNG d'un graphique : au moins 2, quel que soit l'écran (#520). Un écran de
 *  bureau ordinaire est à 1 : son canvas, recopié puis agrandi dans une image de 2 400 px,
 *  donnait une courbe floue. Bornée à 3 : au-delà, l'image pèse sans gagner en netteté. */
export function densiteExport(dpr: number = typeof window !== 'undefined' ? window.devicePixelRatio : 1): number {
  const d = Number.isFinite(dpr) && dpr > 0 ? dpr : 1
  return Math.min(3, Math.max(2, d))
}

/** Le graphique de l'écran, rejoué à `densite` dans un canvas hors écran, sur fond blanc. */
function graphiqueHauteDensite(toile: HTMLCanvasElement, densite = densiteExport()): HTMLCanvasElement {
  const { vivant, largeur, hauteur, complet } = graphiqueVivant(toile)
  const cible = document.createElement('canvas')
  // Taille CSS ici ; Chart.js la multiplie par `devicePixelRatio` (`retinaScale`).
  cible.width = largeur
  cible.height = hauteur
  // Recopié AVANT `destroy()`, qui efface la toile du rejeu.
  return rejouer(vivant, cible, densite, () => surFondBlanc(cible), complet)
}

/**
 * Un graphique. Chart.js dessine dans un canvas 2D ORDINAIRE (ADR-0008) — la contrainte de
 * relecture ne concerne que le WebGL des cartes. Mais le canvas de l'écran est à la densité
 * de l'écran : à 1, sa photo était floue une fois agrandie dans l'image (#520). On REJOUE
 * donc le graphique, comme le SVG (`rejeu.ts`), dans un canvas hors écran à densité ≥ 2 :
 * même mise en page en px CSS, deux fois plus de pixels. Puis sur un fond blanc : le canvas
 * de Chart.js est transparent, et une image PNG transparente s'affiche en noir dans
 * plusieurs messageries.
 */
export function composerGraphique(bloc: SectionChart, toile: HTMLCanvasElement,
                                  ctx: ContexteImage, sources?: string[]): Promise<Blob> {
  let opaque: HTMLCanvasElement
  try {
    opaque = graphiqueHauteDensite(toile)
  } catch (e) {
    return Promise.reject(e)
  }
  const blocs: Bloc[] = [
    { type: 'surtitre', texte: ctx.commune },
    { type: 'titre', texte: bloc.title },
    { type: 'image', source: opaque, largeurSource: opaque.width, hauteurSource: opaque.height },
  ]
  const note = noteOuSource(bloc.note, sources)
  if (note) blocs.push({ type: 'note', texte: note })
  return composerPng(blocs)
}

/**
 * Une carte. Les attributions sont OBLIGATOIRES sur l'image exportée : les fonds viennent
 * d'OpenFreeMap (données OpenStreetMap, ODbL) et de la Géoplateforme IGN (licence ouverte
 * Etalab 2.0), et ces licences exigent la mention. À l'écran elle vit dans le coin de la
 * carte (`carte-attribution`) ; sur l'image elle descend au pied, où elle reste lisible.
 */
export async function composerCarte(map: CarteMapLibre, titre: string, ctx: ContexteImage,
                                    attributionHtml: string, note?: string): Promise<Blob> {
  const toile = await capturerCarte(map)
  const blocs: Bloc[] = [
    { type: 'surtitre', texte: ctx.commune },
    { type: 'titre', texte: titre },
    { type: 'image', source: toile, largeurSource: toile.width, hauteurSource: toile.height },
  ]
  if (note && note.trim()) blocs.push({ type: 'note', texte: note.trim() })
  return composerPng(blocs, { attributions: texteAttribution(attributionHtml) })
}

/**
 * Capture d'une carte MapLibre — SANS `preserveDrawingBuffer`.
 *
 * Un contexte WebGL vide son tampon de dessin dès que le navigateur a présenté l'image :
 * `getCanvas().toDataURL()` appelé « plus tard » rend une image vide ou noire. Les deux
 * remèdes connus :
 *
 *   1. `preserveDrawingBuffer: true` à la création. Coût PERMANENT et payé par les sept
 *      cartes du site à chaque image rendue, qu'on partage ou non : le navigateur doit
 *      conserver une copie du tampon après présentation, ce qui interdit plusieurs
 *      optimisations du compositeur. C'est un prix payé en continu pour un geste rare.
 *
 *   2. Capturer PENDANT l'événement `render`, avant la présentation. Coût nul au repos :
 *      on ne paie qu'au moment du partage. C'est ce qu'on fait ici.
 *
 * `triggerRepaint()` demande une image ; le gestionnaire `render` s'exécute juste après le
 * tracé, alors que le tampon est encore intact ; on y recopie le canvas WebGL dans un
 * canvas 2D, qui, lui, se relit tranquillement.
 */
export function capturerCarte(map: CarteMapLibre): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    let fini = false
    // Borne de sûreté : une tuile qui ne vient jamais ne doit pas laisser le bouton en
    // attente indéfinie. On rend la main avec une erreur, l'appelant la dit à l'utilisateur.
    const minuterie = setTimeout(() => {
      if (fini) return
      fini = true
      reject(new Error('La carte n’a pas fini de s’afficher — réessayez dans un instant.'))
    }, 10_000)

    const capturer = () => {
      if (fini) return
      fini = true
      clearTimeout(minuterie)
      try {
        const source = map.getCanvas()
        const copie = document.createElement('canvas')
        copie.width = source.width
        copie.height = source.height
        const c = copie.getContext('2d')
        if (!c) throw new Error('Canvas 2D indisponible.')
        // Fond blanc d'abord : les fonds de carte ont des zones transparentes (aucune
        // tuile chargée, bordure de l'emprise), qui vireraient au noir dans une messagerie.
        c.fillStyle = '#FFFFFF'
        c.fillRect(0, 0, copie.width, copie.height)
        c.drawImage(source, 0, 0)
        resolve(copie)
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)))
      }
    }

    const declencher = () => {
      map.once('render', capturer)
      map.triggerRepaint()
    }

    // On attend d'abord que les tuiles soient là : capturer une carte à moitié chargée
    // produit une image à trous, et c'est celle-là qui partirait en réunion.
    if (map.loaded()) declencher()
    else map.once('idle', declencher)
  })
}

/** Recopie un canvas sur un fond blanc opaque. */
function surFondBlanc(toile: HTMLCanvasElement): HTMLCanvasElement {
  const copie = document.createElement('canvas')
  copie.width = toile.width
  copie.height = toile.height
  const c = copie.getContext('2d')
  if (!c) return toile
  c.fillStyle = '#FFFFFF'
  c.fillRect(0, 0, copie.width, copie.height)
  c.drawImage(toile, 0, 0)
  return copie
}
